import { useEffect, useLayoutEffect, useRef, useState, type ClipboardEvent, type KeyboardEvent } from "react";
import type { JSX } from "react";
import type { EntityMention, ReplyToContext } from "../../modules/episodes/types";
import { mimeToIcon } from "../../utils/mime";
import type { AppRuntime } from "../../runtime/contracts/runtime";
import {
  useMentionComposer,
  buildPlainTextFragment,
  placeCaretAtEnd,
} from "../../modules/episodes/hooks/useMentionComposer";
import { Icon } from "../ui/Icon";
import { EntityCardRenderer } from "../shared/EntityCardRenderer";
import { MentionPopup } from "../shared/MentionPopup";

export interface ModelSelectorItem {
  readonly id: string;
  readonly label: string;
  readonly section: "agent" | "model";
  readonly engineName: string;
  readonly modelId?: string;
}

export interface PendingAttachment {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
}

export interface ComposerSharedProps {
  readonly isStreaming: boolean;
  readonly disabledReason?: string | null;
  readonly onSend: (text: string, mentions: readonly EntityMention[]) => void;
  readonly onStop: () => void;
  readonly selectorItems?: readonly ModelSelectorItem[];
  readonly currentSelection?: ModelSelectorItem | null;
  readonly onSelectionChange?: (item: ModelSelectorItem) => void;
  readonly replyTo?: ReplyToContext | null;
  readonly onClearReplyTo?: () => void;
  readonly runtime?: AppRuntime;
  readonly onAttachFile?: () => void;
  readonly pendingAttachments?: readonly PendingAttachment[];
  readonly onRemoveAttachment?: (id: string) => void;
}



function ModelSelectionMenuSection({
  title,
  items,
  current,
  onSelect,
}: {
  readonly title: string;
  readonly items: readonly ModelSelectorItem[];
  readonly current?: ModelSelectorItem | null;
  readonly onSelect: (item: ModelSelectorItem) => void;
}): JSX.Element | null {
  if (items.length === 0) return null;

  return (
    <>
      <div className="px-3 pt-2 pb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary">
          {title}
        </span>
      </div>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          className={`flex items-center justify-between w-full px-3 py-2 text-[12px] font-medium cursor-pointer hover:bg-surface-hover ${item.id === current?.id ? "text-content" : "text-content-secondary"}`}
          onClick={() => { onSelect(item); }}
        >
          {item.label}
          {item.id === current?.id && <Icon name="check" size={12} className="text-content" />}
        </button>
      ))}
    </>
  );
}

function PlusMenuDropdown({
  open,
  onToggle,
  onAttachFile,
  selectorItems,
  currentSelection,
  onSelectionChange,
}: {
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onAttachFile?: () => void;
  readonly selectorItems?: readonly ModelSelectorItem[];
  readonly currentSelection?: ModelSelectorItem | null;
  readonly onSelectionChange?: (item: ModelSelectorItem) => void;
}): JSX.Element {
  const [showModelSub, setShowModelSub] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const agents = selectorItems?.filter((item) => item.section === "agent") ?? [];
  const models = selectorItems?.filter((item) => item.section === "model") ?? [];
  const hasSelectionItems = agents.length > 0 || models.length > 0;

  const handleToggle = (): void => {
    onToggle();
    setShowModelSub(false);
  };

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onToggle();
        setShowModelSub(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return (): void => { document.removeEventListener("mousedown", handler); };
  }, [open, onToggle]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-edge bg-transparent text-content-secondary hover:bg-surface-hover hover:text-content transition-colors"
        type="button"
        onClick={handleToggle}
        aria-label="Add context"
      >
        <Icon name="plus" size={16} />
      </button>
      {open && !showModelSub && (
        <div className="absolute bottom-full left-0 mb-1 rounded-lg bg-surface-secondary border border-edge shadow-lg overflow-hidden z-50 min-w-[180px]">
          <button
            type="button"
            className="flex items-center gap-2.5 w-full px-3 py-2.5 text-[13px] text-content-secondary hover:bg-surface-hover"
            onClick={() => {
              onAttachFile?.();
              handleToggle();
            }}
          >
            <Icon name="paperclip" size={15} className="text-content-tertiary" />
            Attach file
          </button>
          {hasSelectionItems && (
            <>
              <div className="border-t border-edge" />
              <button
                type="button"
                className="flex items-center justify-between w-full px-3 py-2.5 text-[13px] text-content-secondary hover:bg-surface-hover"
                onClick={() => { setShowModelSub(true); }}
              >
                <span className="flex items-center gap-2.5">
                  <Icon name="bot" size={15} className="text-content-tertiary" />
                  Model
                </span>
                <span className="flex items-center gap-1 text-[11px] text-content-tertiary">
                  {currentSelection?.label ?? "Select"}
                  <Icon name="chevron-right" size={12} />
                </span>
              </button>
            </>
          )}
        </div>
      )}
      {open && showModelSub && (
        <div className="absolute bottom-full left-0 mb-1 rounded-lg bg-surface-secondary border border-edge shadow-lg overflow-hidden z-50 min-w-[200px]">
          <button
            type="button"
            className="flex items-center gap-2 w-full px-3 py-2 text-[12px] text-content-tertiary hover:bg-surface-hover"
            onClick={() => { setShowModelSub(false); }}
          >
            <Icon name="chevron-left" size={12} />
            Back
          </button>
          <div className="border-t border-edge" />
          <ModelSelectionMenuSection
            title="Agents"
            items={agents}
            current={currentSelection}
            onSelect={(item) => {
              onSelectionChange?.(item);
              handleToggle();
            }}
          />
          {agents.length > 0 && models.length > 0 && (
            <div className="border-t border-edge" />
          )}
          <ModelSelectionMenuSection
            title="Models"
            items={models}
            current={currentSelection}
            onSelect={(item) => {
              onSelectionChange?.(item);
              handleToggle();
            }}
          />
        </div>
      )}
    </div>
  );
}

export function ReplyToBar({
  replyTo,
  runtime,
  onDismiss,
}: {
  readonly replyTo: ReplyToContext | null;
  readonly runtime: AppRuntime;
  readonly onDismiss: () => void;
}): JSX.Element | null {
  if (!replyTo) return null;
  return (
    <div className="flex items-center gap-2 border-b border-edge px-3 py-2">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-content-tertiary mb-1">
          Reply to
        </div>
        <EntityCardRenderer
          schemaId={replyTo.schemaId}
          data={{ ...replyTo.data, id: replyTo.entityId, name: replyTo.name }}
          runtime={runtime}
        />
      </div>
      <button
        type="button"
        className="shrink-0 p-1 rounded hover:bg-surface-hover text-content-tertiary"
        onClick={onDismiss}
        aria-label="Cancel reply"
      >
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}

function PendingAttachmentChips({
  attachments,
  onRemove,
}: {
  readonly attachments: readonly PendingAttachment[];
  readonly onRemove?: (id: string) => void;
}): JSX.Element | null {
  if (attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-3 pb-1.5">
      {attachments.map((att) => (
        <span
          key={att.id}
          data-testid="agent-pending-attachment-chip"
          className="inline-flex items-center gap-1.5 rounded-full bg-surface-hover px-2.5 py-1 text-[12px] text-content-secondary"
        >
          <Icon name={mimeToIcon(att.mimeType)} size={12} className="text-content-tertiary" />
          <span className="max-w-[160px] truncate">{att.name}</span>
          <button
            type="button"
            className="flex items-center justify-center rounded-full hover:bg-surface-tertiary p-0.5"
            onClick={() => onRemove?.(att.id)}
            aria-label={`Remove ${att.name}`}
          >
            <Icon name="close" size={10} />
          </button>
        </span>
      ))}
    </div>
  );
}

function ComposerSendButton({
  isStreaming,
  isEmpty,
  disabledReason,
  onStop,
  onSend,
  sizeClassName = "h-8 w-8",
}: {
  readonly isStreaming: boolean;
  readonly isEmpty: boolean;
  readonly disabledReason?: string | null;
  readonly onStop: () => void;
  readonly onSend: () => void;
  readonly sizeClassName?: string;
}): JSX.Element {
  if (isStreaming) {
    return (
      <button
        className={`flex shrink-0 items-center justify-center rounded-full bg-content-secondary text-surface hover:bg-content transition-colors ${sizeClassName}`}
        type="button"
        aria-label="Stop"
        onClick={onStop}
      >
        <span className="h-3 w-3 rounded-[2px] bg-current" />
      </button>
    );
  }

  const disabled = isEmpty || Boolean(disabledReason);
  return (
    <button
      className={`flex shrink-0 items-center justify-center rounded-full transition-colors ${sizeClassName} ${
        disabled
          ? "bg-content-secondary/50 text-surface/50 cursor-default"
          : "bg-content-secondary text-surface hover:bg-content cursor-pointer"
      }`}
      type="button"
      aria-label="Send"
      onClick={disabled ? undefined : onSend}
      disabled={disabled}
      title={disabledReason ?? undefined}
    >
      <Icon name="arrow-up" size={16} />
    </button>
  );
}

export function AgentLandingComposer({
  isStreaming,
  disabledReason,
  onSend,
  onStop,
  selectorItems,
  currentSelection,
  onSelectionChange,
  replyTo,
  onClearReplyTo,
  runtime,
  onAttachFile,
  pendingAttachments,
  onRemoveAttachment,
}: ComposerSharedProps): JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null);
  const draftHtmlRef = useRef("");
  const mention = useMentionComposer(editorRef);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);

  // Auto-focus the editor when the composer mounts — opening a chat (or the
  // first message docking the composer) should land the caret immediately, no
  // click needed. Deferred to the next frame: a synchronous focus on a
  // just-mounted EMPTY contentEditable is dropped by WebKit (the caret only
  // appeared "later", after unrelated re-renders). rAF runs it after the node
  // is laid out so the focus + caret stick on the first paint.
  useEffect(() => {
    if (isStreaming || disabledReason) return;
    const el = editorRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.focus();
      placeCaretAtEnd(el);
    });
    return (): void => { cancelAnimationFrame(raf); };
    // Mount-only: refocusing on every isStreaming flip would steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncComposerState = (): void => {
    const el = editorRef.current;
    const text = mention.extractContent().text.replace(/\u00A0/g, " ");
    const empty = text.trim() === "";
    draftHtmlRef.current = empty ? "" : (el?.innerHTML ?? "");
    const expanded = !empty && (text.includes("\n") || (el?.scrollHeight ?? 0) > 44);
    setIsEmpty(empty);
    setIsExpanded(expanded);
  };

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || !draftHtmlRef.current || el.innerHTML === draftHtmlRef.current) return;
    // @tested-by: tst_fe_agent_composer_003
    // @invariant: collapsed/expanded layout switches must not drop uncontrolled contentEditable DOM.
    el.innerHTML = draftHtmlRef.current;
    // The collapsed↔expanded swap mounts a fresh editor element; after restoring
    // its content the caret would sit at the START, so continued typing / macOS
    // dictation inserted at the front and REORDERED words. Put it at the end.
    placeCaretAtEnd(el);
  }, [isExpanded]);

  const handleSend = (): void => {
    if (disabledReason) return;
    const { text, mentions } = mention.extractContent();
    if (!text.trim()) return;
    const fileAsMentions: EntityMention[] = (pendingAttachments ?? []).map((att) => ({
      id: att.id,
      name: att.name,
      schemaId: "file.object",
    }));
    onSend(text, [...mentions, ...fileAsMentions]);
    mention.clearAll();
    draftHtmlRef.current = "";
    setIsEmpty(true);
    setIsExpanded(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabledReason) {
      if (event.key === "Enter") event.preventDefault();
      return;
    }
    if (mention.handleKeyDown(event)) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleInput = (): void => {
    mention.handleInput();
    syncComposerState();
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    if (!text) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    // Normalize CRLF + insert clean text-nodes/<br> so paste round-trips exactly
    // (no doubled blank lines, no stray \r). See buildPlainTextFragment.
    const fragment = buildPlainTextFragment(text);
    const lastNode = fragment.lastChild;
    range.insertNode(fragment);
    // Caret right after the inserted content — correct even for a mid-text paste.
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    handleInput();
  };

  return (
    <div className="mx-auto w-full max-w-[720px] relative">
      {mention.isMentionActive && (
        <MentionPopup
          results={mention.searchResults}
          isLoading={mention.isSearchLoading}
          selectedIndex={mention.selectedIndex}
          hasQuery={mention.mentionQuery.length > 0}
          activeCategory={mention.activeCategory}
          onSelect={mention.handleSelect}
          onCategorySelect={mention.handleCategorySelect}
          onCategoryBack={mention.handleCategoryBack}
        />
      )}

      {replyTo && runtime && onClearReplyTo && (
        <div className="mb-2">
          <ReplyToBar replyTo={replyTo} runtime={runtime} onDismiss={onClearReplyTo} />
        </div>
      )}

      {pendingAttachments && pendingAttachments.length > 0 && (
        <div className="mb-2">
          <PendingAttachmentChips attachments={pendingAttachments} onRemove={onRemoveAttachment} />
        </div>
      )}

      <div
        data-testid="agent-landing-composer-shell"
        data-expanded={isExpanded ? "true" : "false"}
        className={`border border-edge bg-surface-tertiary ${isExpanded ? "rounded-[32px] px-4 py-4" : "rounded-full px-3 py-2.5"}`}
      >
        {isExpanded ? (
          <>
            <div className="relative max-h-[320px] overflow-y-auto px-1 py-1">
              {isEmpty && (
                <div className="pointer-events-none absolute left-1 top-1 text-[13px] text-content-tertiary">
                  Message agent, @ for context...
                </div>
              )}
              <div
                ref={editorRef}
                className="w-full border-0 bg-transparent text-content caret-white outline-none text-[13px] leading-6 whitespace-pre-wrap break-words"
                contentEditable={!isStreaming && !disabledReason}
                role="textbox"
                aria-label="Agent prompt"
                aria-multiline="true"
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                suppressContentEditableWarning
              />
            </div>

            <div data-testid="agent-composer-toolbar" className="mt-3 flex items-end justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <PlusMenuDropdown
                  open={plusMenuOpen}
                  onToggle={() => { setPlusMenuOpen((v) => !v); }}
                  onAttachFile={onAttachFile}
                  selectorItems={selectorItems}
                  currentSelection={currentSelection}
                  onSelectionChange={(item) => {
                    onSelectionChange?.(item);
                    setPlusMenuOpen(false);
                  }}
                />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  aria-label="Voice input"
                  disabled
                  className="flex h-8 w-8 items-center justify-center rounded-full text-content-tertiary/70"
                >
                  <Icon name="mic" size={18} />
                </button>
                <ComposerSendButton
                  isStreaming={isStreaming}
                  isEmpty={isEmpty}
                  disabledReason={disabledReason}
                  onStop={onStop}
                  onSend={handleSend}
                  sizeClassName="h-10 w-10"
                />
              </div>
            </div>
          </>
        ) : (
          <div data-testid="agent-composer-toolbar" className="flex items-center gap-2.5">
            <PlusMenuDropdown
              open={plusMenuOpen}
              onToggle={() => { setPlusMenuOpen((v) => !v); }}
              onAttachFile={onAttachFile}
              selectorItems={selectorItems}
              currentSelection={currentSelection}
              onSelectionChange={(item) => {
                onSelectionChange?.(item);
                setPlusMenuOpen(false);
              }}
            />

            <div className="flex-1 min-w-0 overflow-hidden relative py-1">
              {isEmpty && (
                <div className="pointer-events-none absolute left-0 right-0 top-1 truncate text-[13px] text-content-tertiary whitespace-nowrap">
                  Message agent, @ for context...
                </div>
              )}
              <div
                ref={editorRef}
                className="w-full min-h-[20px] max-h-[200px] overflow-y-auto border-0 bg-transparent text-content caret-white outline-none text-[13px] whitespace-pre-wrap break-words"
                contentEditable={!isStreaming && !disabledReason}
                role="textbox"
                aria-label="Agent prompt"
                aria-multiline="true"
                onInput={handleInput}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
                suppressContentEditableWarning
              />
            </div>

            <ComposerSendButton
              isStreaming={isStreaming}
              isEmpty={isEmpty}
              disabledReason={disabledReason}
              onStop={onStop}
              onSend={handleSend}
            />
          </div>
        )}
      </div>
      {disabledReason ? (
        <div
          role="status"
          className="mt-2 px-3 text-[12px] text-content-tertiary"
        >
          {disabledReason}
        </div>
      ) : null}
    </div>
  );
}

export function AgentDockedComposer({
  isStreaming,
  disabledReason,
  onSend,
  onStop,
  selectorItems,
  currentSelection,
  onSelectionChange,
  replyTo,
  onClearReplyTo,
  runtime,
  onAttachFile,
  pendingAttachments,
  onRemoveAttachment,
}: ComposerSharedProps): JSX.Element {
  const editorRef = useRef<HTMLDivElement>(null);
  const draftHtmlRef = useRef("");
  const mention = useMentionComposer(editorRef);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [plusMenuOpen, setPlusMenuOpen] = useState(false);

  // Auto-focus the editor when the composer mounts — opening a chat (or the
  // first message docking the composer) should land the caret immediately, no
  // click needed. Deferred to the next frame: a synchronous focus on a
  // just-mounted EMPTY contentEditable is dropped by WebKit (the caret only
  // appeared "later", after unrelated re-renders). rAF runs it after the node
  // is laid out so the focus + caret stick on the first paint.
  useEffect(() => {
    if (isStreaming || disabledReason) return;
    const el = editorRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.focus();
      placeCaretAtEnd(el);
    });
    return (): void => { cancelAnimationFrame(raf); };
    // Mount-only: refocusing on every isStreaming flip would steal focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncComposerState = (): void => {
    const el = editorRef.current;
    const text = mention.extractContent().text.replace(/\u00A0/g, " ");
    const empty = text.trim() === "";
    draftHtmlRef.current = empty ? "" : (el?.innerHTML ?? "");
    const expanded = !empty && (text.includes("\n") || (el?.scrollHeight ?? 0) > 44);
    setIsEmpty(empty);
    setIsExpanded(expanded);
  };

  useLayoutEffect(() => {
    const el = editorRef.current;
    if (!el || !draftHtmlRef.current || el.innerHTML === draftHtmlRef.current) return;
    // @tested-by: tst_fe_agent_composer_003
    // @invariant: collapsed/expanded layout switches must not drop uncontrolled contentEditable DOM.
    el.innerHTML = draftHtmlRef.current;
    // The collapsed↔expanded swap mounts a fresh editor element; after restoring
    // its content the caret would sit at the START, so continued typing / macOS
    // dictation inserted at the front and REORDERED words. Put it at the end.
    placeCaretAtEnd(el);
  }, [isExpanded]);

  const handleSend = (): void => {
    if (disabledReason) return;
    const { text, mentions } = mention.extractContent();
    if (!text.trim()) return;
    const fileAsMentions: EntityMention[] = (pendingAttachments ?? []).map((att) => ({
      id: att.id,
      name: att.name,
      schemaId: "file.object",
    }));
    onSend(text, [...mentions, ...fileAsMentions]);
    mention.clearAll();
    draftHtmlRef.current = "";
    setIsEmpty(true);
    setIsExpanded(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (disabledReason) {
      if (event.key === "Enter") event.preventDefault();
      return;
    }
    if (mention.handleKeyDown(event)) return;
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleInput = (): void => {
    mention.handleInput();
    syncComposerState();
  };

  const handlePaste = (event: ClipboardEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    if (!text) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    range.deleteContents();
    // Normalize CRLF + insert clean text-nodes/<br> so paste round-trips exactly
    // (no doubled blank lines, no stray \r). See buildPlainTextFragment.
    const fragment = buildPlainTextFragment(text);
    const lastNode = fragment.lastChild;
    range.insertNode(fragment);
    // Caret right after the inserted content — correct even for a mid-text paste.
    if (lastNode) {
      range.setStartAfter(lastNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    handleInput();
  };

  return (
    <footer className="shrink-0 px-4 pb-4 pt-1.5">
      <div className="relative">
        {mention.isMentionActive && (
          <MentionPopup
            results={mention.searchResults}
            isLoading={mention.isSearchLoading}
            selectedIndex={mention.selectedIndex}
            hasQuery={mention.mentionQuery.length > 0}
            activeCategory={mention.activeCategory}
            onSelect={mention.handleSelect}
            onCategorySelect={mention.handleCategorySelect}
            onCategoryBack={mention.handleCategoryBack}
          />
        )}

        {replyTo && runtime && onClearReplyTo && (
          <div className="mb-2">
            <ReplyToBar replyTo={replyTo} runtime={runtime} onDismiss={onClearReplyTo} />
          </div>
        )}

        {pendingAttachments && pendingAttachments.length > 0 && (
          <div className="mb-2">
            <PendingAttachmentChips attachments={pendingAttachments} onRemove={onRemoveAttachment} />
          </div>
        )}

        <div
          data-testid="agent-docked-composer-shell"
          data-expanded={isExpanded ? "true" : "false"}
          className={`border border-edge bg-surface-tertiary ${isExpanded ? "rounded-[32px] px-4 py-4" : "rounded-full px-3 py-2.5"}`}
        >
          {isExpanded ? (
            <>
              <div className="relative max-h-[320px] overflow-y-auto px-1 py-1">
                {isEmpty && (
                  <div className="pointer-events-none absolute left-1 top-1 text-[13px] text-content-tertiary">
                    Message agent, @ for context...
                  </div>
                )}
                <div
                  ref={editorRef}
                  className="w-full border-0 bg-transparent text-content caret-white outline-none text-[13px] leading-6 whitespace-pre-wrap break-words"
                  contentEditable={!isStreaming && !disabledReason}
                  role="textbox"
                  aria-label="Agent prompt"
                  aria-multiline="true"
                  onInput={handleInput}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  suppressContentEditableWarning
                />
              </div>

              <div data-testid="agent-composer-toolbar" className="mt-3 flex items-end justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <PlusMenuDropdown
                    open={plusMenuOpen}
                    onToggle={() => { setPlusMenuOpen((v) => !v); }}
                    onAttachFile={onAttachFile}
                    selectorItems={selectorItems}
                    currentSelection={currentSelection}
                    onSelectionChange={(item) => {
                      onSelectionChange?.(item);
                      setPlusMenuOpen(false);
                    }}
                  />
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    aria-label="Voice input"
                    disabled
                    className="flex h-8 w-8 items-center justify-center rounded-full text-content-tertiary/70"
                  >
                    <Icon name="mic" size={18} />
                  </button>
                  <ComposerSendButton
                    isStreaming={isStreaming}
                    isEmpty={isEmpty}
                    disabledReason={disabledReason}
                    onStop={onStop}
                    onSend={handleSend}
                    sizeClassName="h-10 w-10"
                  />
                </div>
              </div>
            </>
          ) : (
            <div data-testid="agent-composer-toolbar" className="flex items-center gap-2.5">
              <PlusMenuDropdown
                open={plusMenuOpen}
                onToggle={() => { setPlusMenuOpen((v) => !v); }}
                onAttachFile={onAttachFile}
                selectorItems={selectorItems}
                currentSelection={currentSelection}
                onSelectionChange={(item) => {
                  onSelectionChange?.(item);
                  setPlusMenuOpen(false);
                }}
              />

              <div className="flex-1 min-w-0 overflow-hidden relative py-1">
                {isEmpty && (
                  <div className="pointer-events-none absolute left-0 right-0 top-1 truncate text-[13px] text-content-tertiary whitespace-nowrap">
                    Message agent, @ for context...
                  </div>
                )}
                <div
                  ref={editorRef}
                  className="w-full min-h-[20px] max-h-[160px] overflow-y-auto border-0 bg-transparent text-content caret-white outline-none text-[13px] whitespace-pre-wrap break-words"
                  contentEditable={!isStreaming && !disabledReason}
                  role="textbox"
                  aria-label="Agent prompt"
                  aria-multiline="true"
                  onInput={handleInput}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  suppressContentEditableWarning
                />
              </div>

              <ComposerSendButton
                isStreaming={isStreaming}
                isEmpty={isEmpty}
                disabledReason={disabledReason}
                onStop={onStop}
                onSend={handleSend}
              />
            </div>
          )}
        </div>
        {disabledReason ? (
          <div
            role="status"
            className="mt-2 px-3 text-[12px] text-content-tertiary"
          >
            {disabledReason}
          </div>
        ) : null}
      </div>
    </footer>
  );
}
